// ─── ArtifactSearchFilter (ocn-S246-P0) ───────────────────────────
// Search box + data-driven type filter for ArtifactsPanel.
//
// Design contract:
//   - Filter type list is DATA-DRIVEN: derived from `availableTypes` (the
//     keys of artifacts actually present in the list) — no hardcoded enum.
//   - `extraFilterTypes` prop lets future surfaces (fix_library, findings,
//     etc.) register new types without editing this component.
//   - Controlled component: parent (ArtifactsPanel) owns all state.

import { useState } from "react";
import { Search, X } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────

/** One selectable type pill. `value` must match ArtifactMeta.key. */
export interface ArtifactFilterType {
  value: string;
  label: string;
}

export interface ArtifactSearchFilterProps {
  /** Controlled search query */
  query: string;
  onQueryChange: (q: string) => void;
  /** Currently selected type filter (null = show all) */
  activeType: string | null;
  onTypeChange: (t: string | null) => void;
  /** Types derived from the artifacts actually present in the list */
  availableTypes: ArtifactFilterType[];
  /**
   * Additional types to show in the filter row even when no artifact with
   * that key is currently loaded. Pass from the parent when the Artifacts
   * surface expands to new data sources (e.g. fix_library, findings).
   * Deduped with availableTypes by value before rendering.
   */
  extraFilterTypes?: ArtifactFilterType[];
}

// ─── Component ───────────────────────────────────────────────────

export function ArtifactSearchFilter({
  query,
  onQueryChange,
  activeType,
  onTypeChange,
  availableTypes,
  extraFilterTypes = [],
}: ArtifactSearchFilterProps) {
  const [focused, setFocused] = useState(false);

  // Merge availableTypes + extraFilterTypes, deduplicated by value.
  const seen = new Set<string>();
  const allTypes: ArtifactFilterType[] = [];
  for (const t of [...availableTypes, ...extraFilterTypes]) {
    if (!seen.has(t.value)) {
      seen.add(t.value);
      allTypes.push(t);
    }
  }

  const showTypeRow = allTypes.length > 1;

  return (
    <div style={{ padding: '8px 12px 0', flexShrink: 0 }}>
      {/* Search input */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 9px',
          background: 'var(--surface2)',
          border: `1px solid ${focused ? 'var(--border2)' : 'var(--border)'}`,
          borderRadius: 6,
          transition: 'border-color 0.15s',
        }}
      >
        <Search
          size={12}
          style={{ color: 'var(--text-dim)', flexShrink: 0 }}
        />
        <input
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Search artifacts…"
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontSize: 12,
            color: 'var(--text)',
            fontFamily: 'var(--font-sans)',
          }}
        />
        {query && (
          <button
            onClick={() => onQueryChange('')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-dim)',
            }}
            aria-label="Clear search"
          >
            <X size={11} />
          </button>
        )}
      </div>

      {/* Type filter pills — shown only when more than one type exists */}
      {showTypeRow && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            marginTop: 7,
            flexWrap: 'wrap',
          }}
        >
          {/* "All" pill */}
          <TypePill
            label="All"
            active={activeType === null}
            onClick={() => onTypeChange(null)}
          />
          {allTypes.map((t) => (
            <TypePill
              key={t.value}
              label={t.label}
              active={activeType === t.value}
              onClick={() =>
                onTypeChange(activeType === t.value ? null : t.value)
              }
            />
          ))}
        </div>
      )}

      {/* Spacer below filter row */}
      <div style={{ height: 8 }} />
    </div>
  );
}

// ─── TypePill ─────────────────────────────────────────────────────

interface TypePillProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function TypePill({ label, active, onClick }: TypePillProps) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? 'var(--orange)' : 'var(--surface2)',
        color: active ? '#fff' : 'var(--text-dim)',
        border: `1px solid ${active ? 'var(--orange)' : 'var(--border)'}`,
        borderRadius: 12,
        fontSize: 10,
        padding: '2px 8px',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        fontWeight: active ? 600 : 400,
        transition: 'background 0.1s, color 0.1s, border-color 0.1s',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}
